import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import { moca } from 'neuro-schemas';
import * as React from 'react';
import { useIntl } from 'react-intl';
import colors from '../../../../../config/theme/colors';
import { mocaConfig } from './config';
import { referenceText } from '../../../../../config/theme/componentTheme';

const {
  calculateScore,
  calculators: { mocaTaskSpecificScore },
} = moca;

const MOCAForm = ({ formData, viewing }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const optionFormatter =
    (opt: number | string) =>
    (name: number | string): string => {
      return fm(`cognition.moca.${opt}.${name}`);
    };

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!viewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="cognition.unableToPerform">
        <InputHandler
          type="CheckboxSingle"
          name="unableToPerform"
          editing={!viewing}
          formData={formData}
          option={'true'}
          showLabel={false}
          optionFormatter={optionFormatter('unableToPerform')}
          dependentFieldsList={(): string[] => [
            'unableToPerformReason',
            'totalScoreOnly',
            'totalScore',
            'visuospatialExecutive',
            'naming',
            'memory',
            'attentionNumbers',
            'attentionLetters',
            'attentionSubstraction',
            'language',
            'abstraction',
            'delayedRecall',
            'orientation',
            'twelveYeasOrLessEducation',
          ]}
        />
      </FormRow>
      <FormRow title="general.reason" condition={formData.document.unableToPerform === true}>
        <InputHandler
          type="TextArea"
          name="unableToPerformReason"
          formData={formData}
          editing={!viewing}
          placeholder="general.reason"
          width={40}
        />
      </FormRow>
      {formData.document.unableToPerform !== true ? (
        <>
          <FormRow title="cognition.totalScoreOnly">
            <InputHandler
              type="Checkbox"
              editing={!viewing}
              name="totalScoreOnly"
              formData={formData}
              options={['totalScoreOnly']}
              optionFormatter={(name: number | string): string => fm(`cognition.${name}`)}
            />
          </FormRow>
          <FormRow title="cognition.download">
            <a href="/files/MOCA-Test-Finnish.pdf" target="_blank" rel="noopener noreferrer">
              {fm('cognition.downloadHere')}
            </a>
          </FormRow>
          {formData.document.totalScoreOnly?.[0] === 'totalScoreOnly' ? (
            <FormSection>
              <FormRow title="cognition.totalScore">
                <InputHandler
                  type="NumberField"
                  editing={!viewing}
                  name="totalScore"
                  formData={formData}
                  placeholder="cognition.totalScore"
                  staticValue={formData.document.totalScore}
                  min={0}
                  max={30}
                />
              </FormRow>
            </FormSection>
          ) : (
            <>
              <FormSection>
                <>
                  <FormRow title="cognition.moca.description">
                    <div />
                  </FormRow>
                  {mocaConfig.map(
                    (obj: {
                      name: keyof Omit<IMOCA, keyof IControlProps>;
                      options: string[];
                      description: boolean;
                    }) => (
                      <React.Fragment key={obj.name}>
                        {obj.name != 'memory' ? (
                          <FormRow
                            title={`cognition.moca.${obj.name}.title`}
                            description={
                              obj.description === true ? fm(`cognition.moca.${obj.name}.description`) : undefined
                            }
                          >
                            <Container>
                              <Item xs={8}>
                                <InputHandler
                                  type="Checkbox"
                                  editing={!viewing}
                                  name={obj.name}
                                  formData={formData}
                                  options={obj.options}
                                  optionFormatter={optionFormatter(obj.name)}
                                />
                              </Item>
                              <Item xs={4}>
                                {mocaTaskSpecificScore(obj.name, obj.options.length, formData.document)}
                              </Item>
                            </Container>
                          </FormRow>
                        ) : (
                          <FormRow
                            title={`cognition.moca.${obj.name}.title`}
                            description={
                              obj.description === true ? fm(`cognition.moca.${obj.name}.description`) : undefined
                            }
                          >
                            &nbsp;
                          </FormRow>
                        )}
                      </React.Fragment>
                    ),
                  )}
                </>
              </FormSection>
              <FormSection>
                <FormRow
                  title="cognition.moca.twelveYearsOrLessEducation.title"
                  description={fm(`cognition.moca.twelveYearsOrLessEducation.description`)}
                >
                  <InputHandler
                    type="Checkbox"
                    editing={!viewing}
                    name="twelveYearsOrLessEducation"
                    formData={formData}
                    options={['yes']}
                    optionFormatter={(name: number | string): string => fm(`general.${name}`)}
                  />
                </FormRow>
                <FormRow title="cognition.totalScore" description={fm('cognition.moca.totalScoreDescription')}>
                  <div
                    style={{
                      fontSize: '2rem',
                      fontWeight: 600,
                      color: colors.primary,
                      marginRight: '2rem',
                      marginLeft: '2lem',
                    }}
                  >
                    {calculateScore(formData.document) ?? '-'}
                  </div>
                </FormRow>
                <div style={referenceText}>{fm('cognition.moca.referenceText')}</div>
              </FormSection>
              <FormSection>
                <FormRow title="cognition.additionalInfo">
                  <InputHandler
                    name="additionalInfo"
                    type="TextArea"
                    editing={!viewing}
                    placeholder={fm('cognition.additionalInfo')}
                    width={50}
                    formData={formData}
                  />
                </FormRow>
              </FormSection>
            </>
          )}
        </>
      ) : undefined}
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'moca'>>;
  viewing?: boolean;
}

export default MOCAForm;
